import { Icon } from "@iconify/react";
import {
  Button,
  Card,
  Flex,
  Grid,
  Input,
  message,
  Pagination,
  Table,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { testimonyService } from "../../services/testimony.service";
import { testimonyVokasiService } from "../../services/testimony-vokasi.service";
import useTitleStore from "../../store/titleStore";
import { useTableHeight } from "../../hooks/useTableHeight";
import useDebounce from "../../hooks/useDebounce";
import TableAction from "../../components/TableAction";
import UpdatedByCell from "../../components/UpdatedByCell";

export default function TestimonyIndex() {
  const breakpoint = Grid.useBreakpoint();

  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();
  const tableHeight = useTableHeight(420);

  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    keyword: null,
  });

  const {
    data: testimonyData,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["testimony", dataParams],
    queryFn: () => testimonyService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  });

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (id) => testimonyService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries("testimony");
      messageApi.success("Data berhasil dihapus");
    },
    onError: (error) => {
      console.error(error);
      messageApi.error("Terjadi kesalahan saat menghapus data");
    },
  });

  useEffect(() => {
    setTitle("Testimoni");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      keyword: debouncedKeyword,
    }));
  }, [debouncedKeyword]);

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page,
    });
  };

  const columns = [
    {
      title: "No",
      align: "center",
      width: 20,
      render: (text, record, index) =>
        (testimonyData?.pagination.page - 1) * dataParams.limit + index + 1,
      fixed: breakpoint.md && "left",
    },
    {
      title: "Nama",
      dataIndex: "name",
      width: 100,
      fixed: breakpoint.md && "left",
      render: (val) => (
        <Typography.Text style={{ fontWeight: 600 }}>{val}</Typography.Text>
      ),
    },
    {
      title: "Jabatan",
      dataIndex: "position",
      width: 100,
    },
    {
      title: "Direktorat",
      dataIndex: "directorate",
      width: 100,
      render: (val) => val ?? "-",
    },
    {
      title: "Update By",
      width: 100,
      render: (val) => <UpdatedByCell value={val} />,
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      width: 40,
      fixed: "right",
      render: (record) => (
        <TableAction
          onClickEdit={() =>
            navigate("/testimony/detail/" + record.uuid, {
              state: { testimony: record },
            })
          }
          handleDelete={() => handleDelete(record.uuid)}
          deleteLoading={deleteLoading}
        />
      ),
    },
  ];

  return (
    <Card
      styles={{
        body: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      }}
    >
      {contextHolder}
      <Flex
        justify="space-between"
        gap={20}
        style={{ paddingInline: 24, marginBottom: 24 }}
      >
        <Input
          style={{ width: 320 }}
          prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
          placeholder="Cari"
          onChange={(e) => setKeyword(e.target.value)}
          value={keyword}
        />
        <Button
          variant="solid"
          color="primary"
          icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
          onClick={() => navigate("/testimony/create")}
          style={{ minWidth: 38 }}
        >
          {breakpoint.md && "Tambah Testimoni"}
        </Button>
      </Flex>
      <Table
        columns={columns}
        pagination={false}
        size="small"
        loading={isLoading || isRefetching}
        rowKey={"id"}
        scroll={{ y: tableHeight, x: 1000 }}
        dataSource={testimonyData?.data}
      />
      <Pagination
        style={{ marginInline: 24, marginTop: 16 }}
        total={testimonyData?.pagination.total}
        showSizeChanger={{
          showSearch: false,
        }}
        pageSizeOptions={["10", "20", "50"]}
        current={testimonyData?.pagination.page}
        pageSize={dataParams.limit}
        align="end"
        onChange={handleChangePage}
      />
    </Card>
  );
}
